import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { I18n } from 'react-redux-i18n-lite';
import { BottomSheet, SwipeableRow } from '@netexknowledge/smart-components';
import SanitizedString from '../../common/SanitizedString';

// An activity counts for the average unless it was explicitly set as not assessable
const isAssessableOn = activity => activity.assessable !== false;

/**
 * Mobile detail of one gradebook row: the student's state in every column (activity, lesson, unit or task)
 * that the desktop grid paints as a row of icons.
 * Content mode rows swipe (the action is named by the side where it shows):
 * - activity: left "Avaliable" opens the activity view (second view of this sheet), right "Abrir" opens it
 *   in the content;
 * - unit or lesson: left "Subir" goes up a level, right "Baixar" (or a tap) goes down.
 */
const GradebookStudentSheet = ({
  open,
  onClose,
  name,
  avatarUrl,
  grade,
  chipClass,
  rows,
  enterFrom,
  canEditAssessable,
  onToggleAssessable
}) => {
  const [activityRow, setActivityRow] = useState(null);
  const [assessable, setAssessable] = useState(true);
  const [saving, setSaving] = useState(false);
  // Back from the activity view: the list slides in from the left
  const [listEnter, setListEnter] = useState(enterFrom);

  const close = () => {
    setActivityRow(null);
    onClose();
  };

  const openActivity = row => {
    setAssessable(isAssessableOn(row.assessable));
    setActivityRow(row);
  };

  const openContent = row => {
    close();
    row.onOpenContent();
  };

  const toggleAssessable = () => {
    const value = !assessable;
    setAssessable(value);
    setSaving(true);
    Promise.resolve(onToggleAssessable(activityRow.assessable, value)).then(saved => {
      setSaving(false);
      if (saved === false) setAssessable(!value);
    });
  };

  const getActions = row => {
    if (row.assessable) {
      const canEdit = canEditAssessable && !row.assessable.disabled;
      return {
        leftAction: canEdit
          ? { icon: 'evaluable', label: I18n.t('gradebook.mobile.assessable'), onTrigger: () => openActivity(row) }
          : undefined,
        rightAction: row.onOpenContent
          ? { icon: 'share-arrow', label: I18n.t('gradebook.mobile.open'), onTrigger: () => openContent(row) }
          : undefined
      };
    }
    if (row.onClick) {
      return {
        leftAction: row.onLevelUp
          ? { icon: 'up-arrow-sign', label: I18n.t('gradebook.mobile.levelUp'), onTrigger: row.onLevelUp }
          : undefined,
        rightAction: { icon: 'right-direction', label: I18n.t('gradebook.mobile.levelDown'), onTrigger: row.onClick }
      };
    }
    return null;
  };

  const renderRow = row => {
    const content = (
      <>
        {row.typeIcon && <span className={`custom-icons-${row.typeIcon} gradebook-sheet-type`} aria-hidden="true" />}
        <span className="gradebook-sheet-text">
          <span className="gradebook-sheet-name">
            <SanitizedString rawString={row.name} />
          </span>
          {row.label && <span className="gradebook-sheet-status">{row.label}</span>}
        </span>
        <span className="gradebook-sheet-state">{row.statusNode}</span>
        {row.onClick && <i className="custom-icons-right-chevron gradebook-sheet-chevron" aria-hidden="true" />}
      </>
    );
    const element = row.onClick ? (
      <button type="button" className="gradebook-sheet-row" onClick={row.onClick}>
        {content}
      </button>
    ) : (
      <div className="gradebook-sheet-row">{content}</div>
    );
    const actions = getActions(row);
    return <li key={row.key}>{actions ? <SwipeableRow {...actions}>{element}</SwipeableRow> : element}</li>;
  };

  if (activityRow) {
    const activity = activityRow.assessable;
    const disabled = !canEditAssessable || Boolean(activity.disabled) || saving;
    return (
      <BottomSheet
        open={open}
        onClose={close}
        closeLabel={I18n.t('common.close')}
        overline={I18n.t('gradebook.mobile.activity')}
        title={
          <span className="gradebook-activity-title">
            <SanitizedString rawString={activityRow.name} />
          </span>
        }
        onBack={() => {
          setListEnter('up');
          setActivityRow(null);
        }}
        backLabel={I18n.t('common.back')}
      >
        <div className="gradebook-activity-view">
          <div className="gradebook-activity-switch-row">
            <span className="gradebook-activity-switch-text">
              <span id="gradebook-assessable-label" className="gradebook-activity-switch-label">
                {I18n.t('gradebook.mobile.assessable')}
              </span>
              {!assessable && (
                <span className="gradebook-activity-switch-help">{I18n.t('gradebook.mobile.notCounting')}</span>
              )}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={assessable}
              aria-labelledby="gradebook-assessable-label"
              className={assessable ? 'gradebook-switch on' : 'gradebook-switch'}
              disabled={disabled}
              onClick={toggleAssessable}
            >
              <span className="gradebook-switch-thumb" />
            </button>
          </div>
          <p className="gradebook-activity-info">{I18n.t('gradebook.mobile.recalcInfo')}</p>
          {activityRow.onOpenContent && (
            <button type="button" className="gradebook-activity-open" onClick={() => openContent(activityRow)}>
              <i className="custom-icons-share-arrow" aria-hidden="true" />
              {I18n.t('gradebook.mobile.openContent')}
            </button>
          )}
        </div>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet
      open={open}
      onClose={close}
      closeLabel={I18n.t('common.close')}
      title={
        <span className="gradebook-sheet-title">
          <span className="gradebook-sheet-title-name">{name}</span>
          <span className={`gradebook-custom-chip ${chipClass}`}>{grade}</span>
        </span>
      }
      media={avatarUrl ? <img className="gradebook-sheet-avatar" src={avatarUrl} alt="" /> : undefined}
      fullHeight={rows.length > 8}
    >
      <ul className={listEnter ? `gradebook-sheet-list enter-${listEnter}` : 'gradebook-sheet-list'}>
        {rows.map(renderRow)}
      </ul>
    </BottomSheet>
  );
};

GradebookStudentSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  name: PropTypes.string,
  avatarUrl: PropTypes.string,
  grade: PropTypes.string,
  chipClass: PropTypes.string,
  // 'down' or 'up': the list slides in after a level change made from this sheet
  enterFrom: PropTypes.oneOf(['down', 'up']),
  canEditAssessable: PropTypes.bool,
  // (activity, assessable) => saved; false reverts the switch
  onToggleAssessable: PropTypes.func,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      typeIcon: PropTypes.string,
      name: PropTypes.string,
      label: PropTypes.string,
      statusNode: PropTypes.node,
      // Unit or lesson: down a level
      onClick: PropTypes.func,
      // Unit or lesson: up a level, when there is one
      onLevelUp: PropTypes.func,
      // Activity of the selected lesson: the activity data with its assessable flag
      assessable: PropTypes.object,
      onOpenContent: PropTypes.func
    })
  ).isRequired
};

export default GradebookStudentSheet;
